// Filtres à facettes de la barre (ADR-0015) — logique pure.
//
// Chaque facette lit sur un projet zéro, une ou plusieurs valeurs (clé, libellé).
// Les facettes se combinent par ET ; dans une facette, les valeurs cochées se
// combinent par OU (« au moins un ») ou par ET (« tous »), au choix.
//
// Les compteurs suivent la convention des recherches à facettes : pour une
// facette, on compte sur les projets retenus par *toutes les autres* facettes.
// Le nombre affiché à côté d'une valeur dit donc combien de projets on verrait
// en la cochant (en mode « au moins un »), sans que la facette se vide elle-même.

import { clePlan, plansParFiliere } from "./arbres";
import { normaliser } from "./filtres";
import { comparerFr, etatTemporel, groupesBioagresseurs, LIBELLES_ETAT } from "./portefeuille";
import type { DateIso, Portefeuille, Projet } from "./types";

export const IDS_FACETTES = [
  "classe",
  "bioagresseur",
  "levier",
  "culture",
  "filiere",
  "filiereImpliquee",
  "plan",
  "partenaire",
  "typePartenaire",
  "porteur",
  "typePorteur",
  "etat",
] as const;
export type IdFacette = (typeof IDS_FACETTES)[number];

export interface Valeur {
  cle: string;
  libelle: string;
}

export interface DefinitionFacette {
  id: IdFacette;
  libelle: string;
  /** Un projet peut porter plusieurs valeurs : le mode « tous » a un sens. */
  multiple: boolean;
  valeurs: (p: Projet) => Valeur[];
}

export type Mode = "un" | "tous";
export interface Critere {
  valeurs: string[];
  mode: Mode;
}
export type Selection = Partial<Record<IdFacette, Critere>>;

const capitaliser = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const valeur = (texte: string): Valeur => ({ cle: normaliser(texte), libelle: texte.trim() });
const sansDoublon = (valeurs: Valeur[]) => {
  const vues = new Set<string>();
  return valeurs.filter((v) => v.cle && !vues.has(v.cle) && vues.add(v.cle));
};

/** Les facettes, libellées par le référentiel du portefeuille chargé. */
export function definirFacettes(
  portefeuille: Portefeuille,
  aujourdhui: DateIso,
): DefinitionFacette[] {
  const classes = new Map(portefeuille.classesBioagresseur.map((c) => [c.id, c.libelle]));
  const leviers = new Map(portefeuille.leviers.map((l) => [l.id, l.libelle]));
  return [
    {
      id: "classe",
      libelle: "Classe de bioagresseur",
      multiple: true,
      valeurs: (p) => p.bioagresseurs.map((id) => ({ cle: id, libelle: classes.get(id) ?? id })),
    },
    {
      id: "bioagresseur",
      libelle: "Bioagresseur",
      multiple: true,
      // Le groupe (« Lépidoptères ») et chacune de ses espèces : on peut filtrer
      // large ou précis.
      valeurs: (p) =>
        sansDoublon(
          groupesBioagresseurs(p.bioagresseursCommuns)
            .flatMap((g) => [g.nom, ...g.details])
            .map((b) => valeur(capitaliser(b))),
        ),
    },
    {
      id: "levier",
      libelle: "Famille de leviers",
      multiple: true,
      valeurs: (p) => p.leviers.map((id) => ({ cle: id, libelle: leviers.get(id) ?? id })),
    },
    {
      id: "culture",
      libelle: "Culture",
      multiple: true,
      valeurs: (p) =>
        sansDoublon(p.cultures.map((c) => ({ cle: c.cle, libelle: capitaliser(c.nom.trim()) }))),
    },
    {
      id: "filiere",
      libelle: "Filière",
      multiple: false,
      valeurs: (p) => [valeur(p.filiere ?? "Transversal")],
    },
    {
      id: "filiereImpliquee",
      libelle: "Filière impliquée",
      multiple: true,
      valeurs: (p) => sansDoublon(p.filieresImpliquees.map(valeur)),
    },
    {
      id: "plan",
      libelle: "Plan d'action",
      multiple: true,
      valeurs: (p) =>
        sansDoublon(plansParFiliere(p).map((x) => ({ cle: clePlan(x.plan), libelle: x.plan }))),
    },
    {
      id: "partenaire",
      libelle: "Partenaire",
      multiple: true,
      valeurs: (p) => sansDoublon(p.partenaires.map((x) => ({ cle: x.cle, libelle: x.nom }))),
    },
    {
      id: "typePartenaire",
      libelle: "Type de partenaire",
      multiple: true,
      valeurs: (p) => sansDoublon(p.partenaires.map((x) => valeur(x.typeStructure ?? "Autre"))),
    },
    {
      id: "porteur",
      libelle: "Porteur",
      multiple: false,
      valeurs: (p) => (p.porteur ? [valeur(p.porteur)] : []),
    },
    {
      id: "typePorteur",
      libelle: "Type de porteur",
      multiple: false,
      valeurs: (p) => (p.typePorteur ? [valeur(p.typePorteur)] : []),
    },
    {
      id: "etat",
      libelle: "Avancement",
      multiple: false,
      valeurs: (p) => {
        const e = etatTemporel(p, aujourdhui);
        return [{ cle: e, libelle: LIBELLES_ETAT[e] }];
      },
    },
  ];
}

/** Valeurs de chaque projet pour chaque facette, calculées une fois. */
export type LigneIndex = Record<IdFacette, Map<string, string>>;
export type Index = LigneIndex[];

export function indexer(projets: Projet[], facettes: DefinitionFacette[]): Index {
  return projets.map((p) => {
    const ligne = {} as LigneIndex;
    for (const f of facettes) ligne[f.id] = new Map(f.valeurs(p).map((v) => [v.cle, v.libelle]));
    return ligne;
  });
}

const actif = (c: Critere | undefined): c is Critere => !!c && c.valeurs.length > 0;

function satisfait(valeurs: Map<string, string>, c: Critere): boolean {
  return c.mode === "tous"
    ? c.valeurs.every((v) => valeurs.has(v))
    : c.valeurs.some((v) => valeurs.has(v));
}

/** Le projet satisfait-il tous les critères, sauf éventuellement celui d'une facette ? */
function retenu(ligne: LigneIndex, selection: Selection, sauf?: IdFacette) {
  return IDS_FACETTES.every((id) => {
    const c = selection[id];
    return id === sauf || !actif(c) || satisfait(ligne[id], c);
  });
}

/**
 * Indices des projets retenus. `parmi` restreint le départ (la recherche texte
 * de la barre) ; null : tous les projets.
 */
export function filtrer(
  index: Index,
  selection: Selection,
  parmi: ReadonlySet<number> | null = null,
): number[] {
  return index.flatMap((ligne, i) =>
    (parmi === null || parmi.has(i)) && retenu(ligne, selection) ? [i] : [],
  );
}

export interface Compte extends Valeur {
  n: number;
  /** Effectif sans aucun filtre : il fixe l'ordre. */
  total: number;
  coche: boolean;
}

/**
 * Valeurs d'une facette avec leur effectif parmi les projets retenus par les
 * autres facettes. Rangées par effectif total puis par ordre alphabétique, et
 * toutes gardées, même à zéro : cocher une valeur ne déplace ni ne retire les
 * autres sous le pointeur.
 */
export function compter(
  index: Index,
  selection: Selection,
  facette: IdFacette,
  parmi: ReadonlySet<number> | null = null,
): Compte[] {
  const coches = new Set(selection[facette]?.valeurs ?? []);
  const comptes = new Map<string, Compte>();
  index.forEach((ligne, i) => {
    if (parmi !== null && !parmi.has(i)) return;
    const garde = retenu(ligne, selection, facette);
    for (const [cle, libelle] of ligne[facette]) {
      const c = comptes.get(cle) ?? { cle, libelle, n: 0, total: 0, coche: coches.has(cle) };
      c.total++;
      if (garde) c.n++;
      comptes.set(cle, c);
    }
  });
  return [...comptes.values()].sort(
    (a, b) => b.total - a.total || comparerFr(a.libelle, b.libelle),
  );
}

export interface PointCommun extends Valeur {
  facette: IdFacette;
}

/**
 * Ce que partagent tous les projets retenus (au moins deux), hors ce qui a été
 * coché : c'est le rapprochement que la sélection révèle, pas sa définition.
 */
export function pointsCommuns(
  index: Index,
  retenus: number[],
  selection: Selection,
): PointCommun[] {
  if (retenus.length < 2) return [];
  const [premier, ...autres] = retenus.map((i) => index[i]).filter((l) => l !== undefined);
  if (!premier) return [];
  return IDS_FACETTES.flatMap((id) => {
    const coches = new Set(selection[id]?.valeurs ?? []);
    return [...premier[id]]
      .filter(([cle]) => !coches.has(cle) && autres.every((l) => l[id].has(cle)))
      .map(([cle, libelle]) => ({ facette: id, cle, libelle }));
  });
}

// --- adresse --------------------------------------------------------------------

/**
 * `levier=biosolutions,monitoring;culture*=vigne` : une facette par segment, `*=`
 * pour le mode « tous ». Les valeurs sont encodées : une virgule ou un point-virgule
 * dans un nom ne casse rien.
 */
export function selectionVersTexte(selection: Selection): string {
  return IDS_FACETTES.flatMap((id) => {
    const c = selection[id];
    if (!actif(c)) return [];
    return [`${id}${c.mode === "tous" ? "*=" : "="}${c.valeurs.map(encodeURIComponent).join(",")}`];
  }).join(";");
}

export function texteVersSelection(texte: string | null): Selection {
  const selection: Selection = {};
  for (const segment of (texte ?? "").split(";")) {
    const m = /^([a-zA-Z]+)(\*?)=(.*)$/.exec(segment);
    if (!m) continue;
    const id = m[1] as IdFacette;
    if (!IDS_FACETTES.includes(id)) continue;
    const valeurs = (m[3] ?? "")
      .split(",")
      .map((v) => {
        try {
          return decodeURIComponent(v);
        } catch {
          return "";
        }
      })
      .filter(Boolean);
    if (valeurs.length) selection[id] = { valeurs, mode: m[2] ? "tous" : "un" };
  }
  return selection;
}
